import { FONT_FILES, type FontFamily } from '../fonts'
import type { ContentKind } from '../ratios'

/**
 * Jenis template data. `video` = templat ADEGAN: satu TemplateDef per adegan,
 * persis seperti slide carousel, lalu adegan-adegan itu disusun menjadi
 * komposisi HyperFrames oleh video/composition.ts.
 */
export type TemplateKind = ContentKind | 'video'

/** Jenis bersusun (punya kolom per slide/adegan). */
export function isSequence(kind: TemplateKind): kind is 'carousel' | 'video' {
  return kind === 'carousel' || kind === 'video'
}
import { elementNames, stateAt, stateCss, validateMotion, type MotionSpec } from '../video/motion'
import { esc } from './document'
import type { FieldDef, FieldType, Frame, TemplateDef, Values } from './types'

/**
 * Template DATA — HTML/CSS yang ditulis AI dari berkas MD akun
 * (docs/rencana-perombakan.md, "Template dari MD").
 *
 * Berbeda dengan template kode (poster-headline.ts), di sini HTML-nya bukan
 * tulisan kita. Karena itu ada tiga pagar, ketiganya wajib:
 *
 * 1. `validateDataTemplate` MENOLAK (bukan membersihkan) apa pun yang bisa
 *    menjalankan kode atau memuat sumber luar: <script>, atribut on*, URL
 *    eksternal, @import, @font-face, dan seterusnya. Template yang ditolak
 *    tidak pernah dipakai; AI diminta memperbaikinya.
 * 2. Nilai user hanya masuk lewat slot `{{key}}`, dan SELALU di-escape.
 *    Bahasa slotnya sengaja kecil: `{{key}}`, `{{#if key}}…{{else}}…{{/if}}`,
 *    `{{#each key}}…{{item}}…{{/each}}`. Tidak ada slot tanpa escape.
 * 3. `buildDocument` memakai CSP ber-nonce untuk template ini (untrusted):
 *    hanya script milik kita (pengecil teks di bawah) yang boleh jalan.
 *
 * Tata letak mengikuti bingkai lewat variabel CSS: `--w`, `--h` (piksel) dan
 * `--u` (= lebar/1080 px, satuan ukuran). Seluruh HTML dibungkus
 * `<div class="frame is-square|is-portrait|is-tall">`, jadi CSS bisa menyetel
 * tata letak per bentuk bingkai (`.is-tall .judul{…}`). Teks yang panjangnya tak tertebak diberi
 * `data-fit` dan diletakkan di wadah ber-tinggi terbatas `data-fit-box`.
 */

export interface DataField {
  key: string
  label: string
  type: FieldType
  required?: boolean
  maxLength?: number
  default?: string
  sample?: string
}

export interface DataTemplate {
  kind: TemplateKind
  name: string
  fonts: FontFamily[]
  css: string
  html: string
  /** Kolom seluruh desain (poster), atau kolom yang sama untuk semua slide (carousel). */
  fields: DataField[]
  /** Hanya carousel: kolom per slide. Di HTML diakses sama seperti kolom biasa. */
  slideFields?: DataField[]
  /** Hanya carousel: isi contoh per slide untuk preview dan render uji. */
  sampleSlides?: Values[]
  /**
   * Hanya video: PANGGUNG yang hidup sepanjang video, di bawah lapisan adegan
   * (`html`). Slotnya hanya `fields` dan `{{total}}`; elemen yang bergerak
   * diberi `data-el="nama"` dan digerakkan `motion` (video/motion.ts).
   */
  stage?: string
  /** Hanya video: gerak panggung & lapisan adegan. Data, bukan script. */
  motion?: MotionSpec
}

/**
 * Slot bawaan yang selalu ada. `index`/`total` hanya bermakna di carousel dan
 * video; `narration` (kalimat narasi adegan, untuk subtitle) hanya di video.
 */
const SLOT_BAWAAN = ['index', 'total', 'isFirst', 'isLast', 'narration']
const SLOT_EACH = ['item', 'number']

const MAX_HTML = 20_000
const MAX_CSS = 20_000
const KEY = /^[a-z][a-zA-Z0-9]{0,30}$/
const FIELD_TYPES: FieldType[] = ['text', 'longtext', 'list', 'color', 'image']
const FAMILIES = new Set<string>(FONT_FILES.map((f) => f.family))
const GENERIK = new Set(['serif', 'sans-serif', 'monospace', 'system-ui', 'cursive', 'inherit', 'initial'])

// --------------------------------------------------------------------- parser

type Node =
  | { t: 'text'; v: string }
  | { t: 'slot'; key: string }
  | { t: 'if'; key: string; yes: Node[]; no: Node[] }
  | { t: 'each'; key: string; body: Node[] }

const TAG = /\{\{\s*(#if|#each|else|\/if|\/each)?\s*([a-zA-Z@][a-zA-Z0-9]*)?\s*\}\}/g

/** Urai bahasa slot. Melempar dengan pesan yang bisa dibaca AI kalau tidak seimbang. */
export function parseSlots(src: string): Node[] {
  const root: Node[] = []
  const stack: { node: Extract<Node, { t: 'if' | 'each' }>; inElse: boolean }[] = []
  const target = () => {
    const top = stack[stack.length - 1]
    if (!top) return root
    if (top.node.t === 'if') return top.inElse ? top.node.no : top.node.yes
    return top.node.body
  }
  let last = 0
  for (const m of src.matchAll(TAG)) {
    if (m.index! > last) target().push({ t: 'text', v: src.slice(last, m.index) })
    last = m.index! + m[0].length
    const [, op, rawKey] = m
    const key = (rawKey ?? '').replace(/^@/, '')
    if (!op) {
      if (!key) throw new Error('Empty {{ }} slot')
      target().push({ t: 'slot', key })
    } else if (op === '#if' || op === '#each') {
      if (!key) throw new Error(`{{${op}}} needs a field name`)
      const node: Extract<Node, { t: 'if' | 'each' }> =
        op === '#if' ? { t: 'if', key, yes: [], no: [] } : { t: 'each', key, body: [] }
      target().push(node)
      stack.push({ node, inElse: false })
    } else if (op === 'else') {
      const top = stack[stack.length - 1]
      if (!top || top.node.t !== 'if' || top.inElse) throw new Error('{{else}} without a matching {{#if}}')
      top.inElse = true
    } else {
      const top = stack.pop()
      const want = op === '/if' ? 'if' : 'each'
      if (!top || top.node.t !== want) throw new Error(`{{${op}}} without a matching {{#${want}}}`)
    }
  }
  if (stack.length) throw new Error(`Unclosed {{#${stack[stack.length - 1]!.node.t}}}`)
  if (last < src.length) target().push({ t: 'text', v: src.slice(last) })
  return root
}

function slotNames(nodes: Node[], out = new Set<string>()): Set<string> {
  for (const n of nodes) {
    if (n.t === 'slot') out.add(n.key)
    else if (n.t === 'if') {
      out.add(n.key)
      slotNames(n.yes, out)
      slotNames(n.no, out)
    } else if (n.t === 'each') {
      out.add(n.key)
      slotNames(n.body, out)
    }
  }
  return out
}

function renderNodes(nodes: Node[], scope: Record<string, string>): string {
  let out = ''
  for (const n of nodes) {
    if (n.t === 'text') out += n.v
    else if (n.t === 'slot') out += esc(scope[n.key] ?? '')
    else if (n.t === 'if') out += renderNodes(scope[n.key] ? n.yes : n.no, scope)
    else {
      const items = (scope[n.key] ?? '').split('\n').map((s) => s.trim()).filter(Boolean)
      items.forEach((item, i) => {
        out += renderNodes(n.body, { ...scope, item, number: String(i + 1) })
      })
    }
  }
  return out
}

// ------------------------------------------------------------------ validator

/**
 * Periksa template dari AI. Mengembalikan daftar masalah (kosong = sah).
 * Pesannya ditujukan ke AI, karena daftar ini dikirim balik untuk perbaikan.
 */
export function validateDataTemplate(raw: unknown): { template: DataTemplate | null; errors: string[] } {
  const errors: string[] = []
  const t = (raw ?? {}) as Record<string, unknown>
  const kind: TemplateKind | null = t.kind === 'carousel' || t.kind === 'poster' || t.kind === 'video' ? t.kind : null
  if (!kind) errors.push('kind must be "poster", "carousel" or "video"')
  const html = typeof t.html === 'string' ? t.html : ''
  const css = typeof t.css === 'string' ? t.css : ''
  if (!html.trim()) errors.push('html is empty')
  if (html.length > MAX_HTML) errors.push(`html is longer than ${MAX_HTML} characters`)
  if (css.length > MAX_CSS) errors.push(`css is longer than ${MAX_CSS} characters`)

  const fields = bacaKolom(t.fields, 'fields', errors)
  const bersusun = kind !== null && isSequence(kind)
  const slideFields = bersusun ? bacaKolom(t.slideFields, 'slideFields', errors) : []
  if (bersusun && !slideFields.length) errors.push(`a ${kind} needs at least one slideFields entry`)

  const fonts = Array.isArray(t.fonts) ? t.fonts.filter((f): f is FontFamily => typeof f === 'string' && FAMILIES.has(f)) : []
  if (Array.isArray(t.fonts) && fonts.length !== t.fonts.length) {
    errors.push(`fonts may only use: ${[...FAMILIES].join(', ')}`)
  }

  // --- HTML: tag & atribut berbahaya ditolak, bukan dibuang.
  periksaHtml(html, '', errors)
  const stage = kind === 'video' && typeof t.stage === 'string' && t.stage.trim() ? t.stage : ''
  if (t.stage !== undefined && kind !== 'video') errors.push('stage is only for video templates')
  if (stage.length > MAX_HTML) errors.push(`stage is longer than ${MAX_HTML} characters`)
  if (stage) periksaHtml(stage, 'stage: ', errors)
  // Atribut style inline ikut aturan CSS.
  const inlineCss = [...`${html}\n${stage}`.matchAll(/\sstyle\s*=\s*"([^"]*)"/gi)].map((m) => m[1]!).join(';')
  periksaCss(`${css};${inlineCss}`, errors)
  if (kind === 'video') {
    // Animasi CSS berjalan menurut jam browser, bukan timeline yang di-seek
    // per frame: hasilnya beda antara Container dan agent, atau beku.
    const c = `${css};${inlineCss}`.toLowerCase()
    if (/@keyframes|(?:^|[;{\s])(?:-webkit-)?(?:animation|transition)(?:-[a-z]+)?\s*:/.test(c)) {
      errors.push('CSS animation and transition are not allowed in video — movement comes only from data-enter, data-kenburns and motion')
    }
  }

  // --- Motion: hanya video, nama elemen harus ada di panggung.
  let motion: MotionSpec | null = null
  if (t.motion !== undefined) {
    if (kind !== 'video') errors.push('motion is only for video templates')
    else {
      const els = elementNames(stage)
      errors.push(...els.errors)
      const v = validateMotion(t.motion, els.names)
      errors.push(...v.errors)
      motion = v.motion
    }
  }

  // --- Slot: seimbang dan hanya merujuk kolom yang dideklarasikan.
  let nodes: Node[] = []
  try {
    nodes = parseSlots(html)
  } catch (err) {
    errors.push(`template tags: ${(err as Error).message}`)
  }
  const dikenal = new Set([...fields, ...slideFields].map((f) => f.key))
  for (const name of slotNames(nodes)) {
    if (!dikenal.has(name) && !SLOT_BAWAAN.includes(name) && !SLOT_EACH.includes(name)) {
      errors.push(`{{${name}}} is not a declared field`)
    }
  }
  if (stage) {
    // Panggung dirender SEKALI untuk seluruh video: kolom per adegan dan
    // slot adegan (index, narration, …) tidak bermakna di sana.
    try {
      const global = new Set([...fields.map((f) => f.key), 'total'])
      for (const name of slotNames(parseSlots(stage))) {
        if (!global.has(name) && !SLOT_EACH.includes(name)) {
          errors.push(`stage: {{${name}}} is not allowed — the stage is shared by every scene, so it may only use fields and {{total}}`)
        }
      }
    } catch (err) {
      errors.push(`stage template tags: ${(err as Error).message}`)
    }
  }
  for (const f of [...fields, ...slideFields]) {
    if (f.type === 'image' && !new RegExp(`\\{\\{\\s*${f.key}\\s*\\}\\}`).test(`${html}\n${stage}`)) {
      errors.push(`image field "${f.key}" is never used`)
    }
  }

  const sampleSlides = Array.isArray(t.sampleSlides)
    ? (t.sampleSlides as unknown[]).slice(0, 12).map((s) => bersihkanNilai(s, slideFields))
    : []
  if (bersusun && sampleSlides.length < 2) errors.push(`a ${kind} needs at least 2 sampleSlides`)

  const uniq = [...new Set(errors)]
  if (uniq.length || !kind) return { template: null, errors: uniq }
  return {
    template: {
      kind,
      name: typeof t.name === 'string' && t.name.trim() ? t.name.trim().slice(0, 60) : kind === 'poster' ? 'Poster' : kind === 'video' ? 'Video' : 'Carousel',
      fonts: fonts.length ? [...new Set(fonts)] : ['Inter'],
      css,
      html,
      fields,
      ...(bersusun ? { slideFields, sampleSlides } : {}),
      ...(stage ? { stage } : {}),
      ...(motion ? { motion } : {}),
    },
    errors: [],
  }
}

function periksaHtml(html: string, label: string, errors: string[]) {
  const lower = html.toLowerCase()
  for (const tag of ['script', 'style', 'link', 'meta', 'base', 'iframe', 'frame', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select', 'foreignobject', 'template', 'audio', 'video', 'source', 'image']) {
    if (new RegExp(`<\\s*${tag}[\\s>/]`).test(lower)) errors.push(`${label}<${tag}> is not allowed`)
  }
  if (/\son[a-z]+\s*=/.test(lower)) errors.push(`${label}event handler attributes (on…=) are not allowed`)
  if (/javascript:|vbscript:/.test(lower)) errors.push(`${label}javascript: URLs are not allowed`)
  if (/\ssrcset\s*=/.test(lower)) errors.push(`${label}srcset is not allowed`)
  for (const m of html.matchAll(/\s(?:xlink:)?href\s*=\s*["']?([^"'\s>]*)/gi)) {
    if (!m[1]!.startsWith('#')) errors.push(`${label}href may only point inside the document (#id)`)
  }
  for (const m of html.matchAll(/\ssrc\s*=\s*["']?([^"'\s>]*)/gi)) {
    if (!/^\{\{\s*[a-zA-Z][a-zA-Z0-9]*\s*\}\}$/.test(m[1]!)) errors.push(`${label}src must be exactly an image slot like src="{{photo}}"`)
  }
}

function bacaKolom(raw: unknown, nama: string, errors: string[]): DataField[] {
  if (raw === undefined) return []
  if (!Array.isArray(raw)) {
    errors.push(`${nama} must be an array`)
    return []
  }
  const out: DataField[] = []
  const seen = new Set<string>()
  for (const r of raw.slice(0, 20) as Record<string, unknown>[]) {
    const key = typeof r?.key === 'string' ? r.key : ''
    const type = r?.type as FieldType
    if (!KEY.test(key)) {
      errors.push(`${nama}: "${String(r?.key)}" is not a valid key (camelCase letters and digits)`)
      continue
    }
    if (SLOT_BAWAAN.includes(key) || SLOT_EACH.includes(key)) {
      errors.push(`${nama}: "${key}" is reserved`)
      continue
    }
    if (seen.has(key)) {
      errors.push(`${nama}: duplicate key "${key}"`)
      continue
    }
    if (!FIELD_TYPES.includes(type)) {
      errors.push(`${nama}.${key}: type must be one of ${FIELD_TYPES.join(', ')}`)
      continue
    }
    seen.add(key)
    const teks = (v: unknown, max: number) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : undefined)
    const f: DataField = { key, label: teks(r.label, 40) ?? key, type }
    if (r.required === true) f.required = true
    if (type === 'text' || type === 'longtext' || type === 'list') {
      const max = Number(r.maxLength)
      f.maxLength = Number.isFinite(max) && max > 0 ? Math.min(Math.round(max), 600) : type === 'text' ? 120 : 400
    }
    const def = teks(r.default, 600)
    if (def !== undefined) {
      if (type === 'color' && !/^#[0-9a-f]{6}$/i.test(def)) errors.push(`${nama}.${key}: color default must be like #1a1a1a`)
      else if (type !== 'image') f.default = def
    }
    const sample = teks(r.sample, 600)
    if (sample !== undefined && type !== 'image' && type !== 'color') f.sample = sample
    out.push(f)
  }
  return out
}

function bersihkanNilai(raw: unknown, fields: DataField[]): Values {
  const r = (raw ?? {}) as Record<string, unknown>
  const out: Values = {}
  for (const f of fields) {
    const v = r[f.key]
    if (typeof v === 'string' && v.trim() && f.type !== 'image') out[f.key] = v.trim().slice(0, f.maxLength ?? 600)
  }
  return out
}

function periksaCss(css: string, errors: string[]) {
  const c = css.toLowerCase()
  if (c.includes('@import')) errors.push('@import is not allowed')
  if (c.includes('@font-face')) errors.push('@font-face is not allowed — fonts are provided')
  if (/expression\s*\(|behavior\s*:|-moz-binding|javascript:/.test(c)) errors.push('legacy script-in-CSS features are not allowed')
  if (/<\s*\/?\s*style/.test(c)) errors.push('css must not contain <style> tags')
  for (const m of css.matchAll(/url\(\s*(['"]?)([^'")]*)\1\s*\)/gi)) {
    const u = m[2]!.trim()
    if (!/^\{\{\s*[a-zA-Z][a-zA-Z0-9]*\s*\}\}$/.test(u) && !u.startsWith('#') && !u.startsWith('data:image/')) {
      errors.push('url() may only use an image slot like url("{{photo}}")')
    }
  }
  // Font di luar daftar tidak menimbulkan error di browser — teks diam-diam
  // jatuh ke font cadangan. Ditolak di sini supaya terlihat.
  for (const m of css.matchAll(/font-family\s*:\s*([^;}]+)/gi)) {
    for (const part of m[1]!.split(',')) {
      const nama = part.trim().replace(/^['"]|['"]$/g, '')
      if (nama && !FAMILIES.has(nama) && !GENERIK.has(nama.toLowerCase()) && !nama.startsWith('var(')) {
        errors.push(`font "${nama}" is not available — use ${[...FAMILIES].join(', ')}`)
      }
    }
  }
}

// -------------------------------------------------------------------- adaptor

/**
 * Pengecil teks generik. Setiap elemen `data-fit` dikecilkan sampai wadahnya
 * (`data-fit-box` terdekat, atau induknya) tidak meluap. Lompatan pertama
 * memakai akar rasio tinggi — sama dengan poster-headline — karena setiap
 * pengukuran memaksa tata letak ulang dan mahal di browser server.
 */
export const FIT_SCRIPT = `window.__fit=function(){
  document.querySelectorAll('[data-fit]').forEach(function(el){
    var box=el.closest('[data-fit-box]')||el.parentElement; if(!box)return;
    var luap=function(){return box.scrollHeight>box.clientHeight+1||box.scrollWidth>box.clientWidth+1};
    if(!luap())return;
    var size=parseFloat(getComputedStyle(el).fontSize),min=Math.max(10,size*0.35);
    var set=function(s){size=Math.max(min,Math.floor(s));el.style.fontSize=size+'px'};
    set(size*Math.sqrt(box.clientHeight/Math.max(box.scrollHeight,1)));
    while(luap()&&size>min)set(size*0.95);
  });
};`

export function bentukBingkai(frame: Frame): string {
  const aspect = frame.height / frame.width
  return aspect <= 1.05 ? 'is-square' : aspect <= 1.6 ? 'is-portrait' : 'is-tall'
}

/**
 * Jadikan template data sebagai TemplateDef biasa, supaya preview browser,
 * buildDocument, dan render server memakainya tanpa jalur kedua.
 *
 * Untuk carousel, satu TemplateDef = satu slide: kolomnya gabungan kolom
 * global dan kolom slide, plus `index`/`total` dari argumen.
 */
export function toTemplateDef(
  dt: DataTemplate,
  ref: { code: string; version: number },
  slide?: { index: number; total: number; narration?: string; layer?: 'beat' },
): TemplateDef {
  const nodes = parseSlots(dt.html)
  const fields: FieldDef[] = [...dt.fields, ...(isSequence(dt.kind) ? (dt.slideFields ?? []) : [])]
  return {
    code: ref.code,
    version: ref.version,
    kind: dt.kind,
    name: dt.name,
    fonts: dt.fonts,
    fields,
    untrusted: true,
    render(values: Values, frame: Frame) {
      const index = slide?.index ?? 1
      const total = slide?.total ?? 1
      const scope: Record<string, string> = {
        ...values,
        index: String(index),
        total: String(total),
        isFirst: index === 1 ? '1' : '',
        isLast: index === total ? '1' : '',
        narration: slide?.narration ?? '',
      }
      const css = `:root{--w:${frame.width}px;--h:${frame.height}px;--u:${(frame.width / 1080).toFixed(5)}px}\n${dt.css}`
      if (dt.kind === 'video' && dt.stage) {
        const kotak = `width:${frame.width}px;height:${frame.height}px;position:absolute;left:0;top:0;overflow:hidden`
        const beat = `<div class="frame beat ${bentukBingkai(frame)}" style="${kotak}">${renderNodes(nodes, scope)}</div>`
        // Komposisi: hanya lapisan adegan; panggung dan geraknya disusun
        // video/composition.ts.
        if (slide?.layer === 'beat') return { css: `${css}\n${STAGE_CSS}`, body: beat, script: FIT_SCRIPT }
        // Preview satu adegan: panggung dalam keadaan beat ini, adegan di atasnya.
        const st = stateCss(stateAt(dt.motion, index - 1, total), frame.width / 1080)
        const body = `<div style="width:${frame.width}px;height:${frame.height}px;position:relative;overflow:hidden">${renderStage(dt, values, frame, total)}${beat}</div>`
        return { css: `${css}\n${STAGE_CSS}\n${st}`, body, script: FIT_SCRIPT }
      }
      const body = `<div class="frame ${bentukBingkai(frame)}" style="width:${frame.width}px;height:${frame.height}px;position:relative;overflow:hidden">${renderNodes(nodes, scope)}</div>`
      return { css, body, script: FIT_SCRIPT }
    },
  }
}

/**
 * Lapisan adegan di atas panggung selalu tembus pandang — latar milik
 * panggung. Template memberi gaya `.frame` untuk keduanya (padding, font);
 * `.stage` / `.beat` untuk yang khusus salah satunya.
 */
export const STAGE_CSS = '.frame.beat{background:none!important;box-shadow:none!important}'

/** Panggung video, dirender sekali dengan kolom global (tanpa nilai per adegan). */
export function renderStage(dt: DataTemplate, values: Values, frame: Frame, total: number): string {
  if (!dt.stage) return ''
  const scope: Record<string, string> = { total: String(total) }
  for (const f of dt.fields) if (values[f.key] !== undefined) scope[f.key] = values[f.key]!
  return `<div class="frame stage ${bentukBingkai(frame)}" style="width:${frame.width}px;height:${frame.height}px;position:absolute;left:0;top:0;overflow:hidden">${renderNodes(parseSlots(dt.stage), scope)}</div>`
}

/** Nilai contoh untuk preview: `sample` tiap kolom, lalu `default`. */
export function sampleValues(fields: readonly DataField[]): Values {
  const v: Values = {}
  for (const f of fields) {
    const s = f.sample ?? f.default
    if (s) v[f.key] = s
  }
  return v
}
